import { AccountSourceCache } from '../../lib/cache/AccountNameCache';
import { Codes, MarketplaceWorkerNotificationType } from '../smartsend/SmartSendSocketsModels';

type SocketDescriptionProps = {
  Code: string;
  MarketplaceSource: string;
  Stage: MarketplaceWorkerNotificationType;
};

export const GetSocketDescription = (props: SocketDescriptionProps): string => {
  const name = AccountSourceCache[props?.MarketplaceSource] || props?.MarketplaceSource;

  switch (props?.Code) {
    case 'USER-ACTION-ACCOUNT-PUT-PERMISSIONS':
      if (props.MarketplaceSource === 'prestashop') {
        return `Please ensure the 'MODIFY (PUT)' permission checkbox is turned on for 'ORDERS' within your Prestashop admin portal. This is required to send tracking numbers to Prestashop.`;
      } else {
        return `Please ensure you have set the correct permissions to access order details within your eCommerce portal. This is required to update tracking numbers.`;
      }

    case 'USER-ACTION-STORE-UNAVAILABLE':
      return `Your store appears to be unavailable. This could happen when you cancel your store subscription, or when there are outstanding issues with your account.`;

    case 'USER-ACTION-PRODUCT-FREEZE':
      if (props.MarketplaceSource === 'tiktok') {
        return 'Please complete your SKU application within TikTok to enable sending tracking numbers.';
      } else {
        return 'Please check the product you are trying to dispatch is still available.';
      }

    case Codes.ImportThrottled:
      return 'Your account is currently being limited by your eCommerce provider. Please try again later.';

    case Codes.SetupRequired:
      return `Please complete the account setup to start importing marketplace orders.`;

    case Codes.AuthorisationFailed:
    case Codes.CredentialsExpired:
    case Codes.ScopeMissing:
      return `Please relink your account to continue importing marketplace orders.`;

    case Codes.WiderIssue:
      return props?.Stage === 'update'
        ? `There is currently an issue in passing tracking details across to ${name} for sold items. We are working on the issue with ${name} as a priority and will update you as soon as we have more information. Apologies for the inconvenience.`
        : `There is currently an issue importing your ${name} items. We are working on the issue as a priority and will update you as soon as we have more information. Apologies for the inconvenience.`;

    case Codes.AutoDispatchFail:
      return `Failed to update tracking details on ${name} for some of your orders. These can be retried from Notify Marketplace`;

    default:
      return null;
  }
};

export const GetSocketLink = (props: { Code: string; MarketplaceSource: string; Links: { [key: string]: string } }) => {
  let objLink = '';
  if (props.Links) {
    switch (props.Code) {
      case Codes.SetupRequired:
        objLink = props.Links['Configure'];
        break;

      case Codes.CredentialsExpired:
      case Codes.AuthorisationFailed:
      case Codes.ScopeMissing:
        objLink = props.Links['Renew'];
        break;
    }
  }

  return objLink;
};

export const GetSocketLinkText = (props: { Code: string }) => {
  switch (props.Code) {
    case Codes.SetupRequired:
      return 'Configure';

    case Codes.CredentialsExpired:
    case Codes.AuthorisationFailed:
    case Codes.ScopeMissing:
      return 'Relink';

    case Codes.AutoDispatchFail:
      return 'Marketplace Tracking';

    default:
      return null;
  }
};

export const GetSocketTitle = (props: { Code: string; MarketplaceSource: string }) => {
  switch (props?.Code) {
    case Codes.SetupRequired:
      return `Account Setup Required`;

    case Codes.AuthorisationFailed:
    case Codes.CredentialsExpired:
    case Codes.ScopeMissing:
      return `Account Relink Required`;

    case Codes.WiderIssue:
      return `Site Issues`;

    default:
      return `Action Required`;
  }
};
